import { Injectable } from "@angular/core";
import { PCCFieldOption, PCCField, EhrType, SnfPatientDetails, ReadStatus} from 'list-types-public';
import { ServerTokenService } from "../services/server-token.service";
import { CustomAuthServiceService } from "../auth/custom-auth-service.service";
import { environment } from "../environment/environment";
import { FacilityService } from "./facility.service";
import { ClientSettingsService } from "./client-settings.service";
import { ClientSettings, isMessagesVisibleToAllFacilities } from "./client-settings.types";


@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  pccPushScreenV2 = {} as any;
  pushSettings: {options: PCCFieldOption[], pccFields: PCCField[], emergancyContact?: { relationsType: { listValue: string, listOption: string }[] }};

  ehrNames: string[] = ['AllScrpits', 'Navi', 'Epic WellStar', 'Epic Piedmont', 'Epic HealthEConnection', EhrType.Manual, EhrType.Efax];
  selectFields: PCCField[] = [];


  constructor(
    private server: ServerTokenService,
    private auth: CustomAuthServiceService,
    public facilityService: FacilityService,
    private clientSettingsService: ClientSettingsService,
  ) {
    auth.authState$.subscribe((state) => {
      if (state) {
        this.getPccSettings();
      }
    });
  }

  get clientSettings(): ClientSettings {
    return this.clientSettingsService.settings;
  }

  /**
   * Merge a `uiSettings` patch into the cached client settings. Never assign to
   * `clientSettings` or its members directly — it is a read-through view of
   * ClientSettingsService, so an in-place write skips the `settings$` emission.
   */
  patchUiSettings(patch: Partial<ClientSettings['uiSettings']>): void {
    this.clientSettingsService.patchUiSettings(patch);
  }

  async refreshSettings() {
    await this.facilityService.refreshSettings();

    await this.getPccSettings();

    await this.clientSettingsService.refresh().toPromise();
  }

  //async updateSettings(settingsJsonObject: FacilitiySettings) {
  //  this.settings = new FacilitiesService(settingsJsonObject);
  //  this.sites = this.settings.sites;
  //  this.allSites = this.settings.facilities?.allNames ?? this.sites;
  //  this.sitedIds = this.settings.sitedIds;
  //  this.allSiteIds = this.settings.allSiteIds;
  
  //  const ehrNames = Object.keys(this.settings?.EHR?.names ?? {})
  //    .filter(name => ![EhrType.Manual, EhrType.Efax].includes(name as EhrType))
  //    .sort((a, b) => a.localeCompare(b));
  
    
  //  if (this.settings?.EHR?.names?.[EhrType.Efax]) {
  //    ehrNames.push(EhrType.Efax);
  //  }
  
  //  if (this.settings?.EHR?.names?.[EhrType.Manual]) {
  //    ehrNames.push(EhrType.Manual);
  //  }
  
  //  this.ehrNames = ehrNames;
  //  this.allSiteNames = Object.keys(this.sites);
  
    
  //  this.selectFields = settingsJsonObject?.selectFields ?? [];
  //}


  updateStatus(basePatientInfo: SnfPatientDetails) {
    if (!basePatientInfo.userLastTimeView) {
      basePatientInfo.readStatus = ReadStatus.NEVER_READ;
    } else {
      if (new Date(basePatientInfo.userLastTimeView) < new Date(basePatientInfo.lastTimeUpdate)) {
        basePatientInfo.readStatus = ReadStatus.READ_BEFORE_UPDATES;
      } else {
        basePatientInfo.readStatus = ReadStatus.READ;
      }
    }

  }

  /** Whether the global UI setting allows the new Job Info page. */
  isJobInfoNewVersionGloballyEnabled(): boolean {
    return this.clientSettings.uiSettings?.newVersionPages?.jobInfo === true;
  }
  /**
   * Single source of truth for Job Info page version.
   * When global allows: use user preference (true → /info, false → /info-old; undefined treated as true).
   * When global disallows: always old (/info-old).
   */
  isJobInfoNewVersion(): boolean {
    if (!this.isJobInfoNewVersionGloballyEnabled()) {
      return false;
    }
    const userPref = this.auth.currentUserValue()?.settings?.uiSettings?.newVersionPages?.jobInfo;
    return userPref !== false;
  }

  getPatientInfoPath(referalId: string | number): string {
    const isPlatformAnywayNew = (environment.env == 'staging');
    const basePath = ( this.isJobInfoNewVersion() || isPlatformAnywayNew || environment.snfClient == environment.CLIENTS_NAMES.ECARE || environment.snfClient == environment.CLIENTS_NAMES.CORECARE) ? 'info' : 'info-old'; //(isPlatformAnywayNew || this.isJobInfoNewVersion() || true ? 'info' : 'info-old');
    return `/manageb/info/${referalId}`;
  }

  isReferralResponseEnabled(srcType: string): boolean {
    return this.clientSettings.uiSettings?.referralResponse?.[srcType] === true;
  }

  /**
   * Whether this region requires PER-USER EHR credentials for the given srcType:
   * an enabled uiSettings.externalAccounts entry whose `type` equals it exactly.
   * Regions responding with a unified (region-level) account have no such entry —
   * the popup's credential gate must not run for them. Matched against the entry
   * VALUES' `type` (the secret-manager type), not the record keys.
   */
  isExternalAccountEnabled(srcType: string): boolean {
    const externalAccounts = this.clientSettings.uiSettings?.externalAccounts;
    return Object.values(externalAccounts ?? {})
      .some(accountConfig => accountConfig?.isEnabled === true && accountConfig?.type === srcType);
  }

  /**
   * Whether SMS is available for the current region. Sourced from the backend
   * `getClientSettings` region UI settings (read fresh server-side), so it
   * updates without a client rebuild. Drives SMS UI visibility only. Defaults
   * to false.
   */
  isSmsEnabled(): boolean {
    return this.clientSettings.uiSettings?.smsEnabled === true;
  }

  // Opt-in: absent ⇒ disabled. Existing clients are switched on by the one-off backfill
  // in WorkflowServer#373, which must run post-deploy or every client sees "not configured".
  isAiDecisionLogicEnabled(): boolean {
    return this.clientSettings.uiSettings?.aiDecisionLogicEnabled === true;
  }

  // Opt-in: absent ⇒ disabled, so every region starts with the internal decline
  // reasons feature (picker + admin catalog editing) hidden until an admin toggles it.
  isInternalDeclineReasonsEnabled(): boolean {
    return this.clientSettings.uiSettings?.internalDeclineReasonsEnabled === true;
  }

  // Opt-in: absent ⇒ disabled. Only regions with a Specialty Rx pharmacy contract get
  // the on-demand medication price quote.
  isMedicationPricingEnabled(): boolean {
    return this.clientSettings.uiSettings?.medicationPricingEnabled === true;
  }

  isMessagesVisibleToAllFacilities(): boolean {
    return isMessagesVisibleToAllFacilities(this.clientSettings.uiSettings);
  }

  isNewReferralInboxEnabled(): boolean {
    return this.clientSettings.uiSettings?.newReferralInbox === true;
  }

  isPageNewVersionGloballyEnabled(pageKey: string): boolean {
    // return this.clientSettings.uiSettings?.newVersionPages?.[pageKey] === true;
    return true;
  }

  async getPccSettings() {
    try {

      const res2 = await this.server.getWithToken('snfs/getPccV2Settings').toPromise();
      if(res2) {
        this.pccPushScreenV2 = res2;
        environment.pccPushScreenV2 = true;
        return true;
      }
    } catch (error) {
      console.error(error);
    }

    const res = await this.server.postWithToken('snfs/getPushSettings', {}).toPromise();
    if(res) {
      this.pushSettings = res as { options: PCCFieldOption[], pccFields: PCCField[] };
    }
  }
}
